<script>
  import { createEventDispatcher } from "svelte";
  import { InlineLoading } from "carbon-components-svelte";
  import Modal from "lib/components/Modal.svelte";
  import Button from "lib/components/Button.svelte";
  import DataTableBlack from "lib/components/DataTableBlack.svelte";
  import SearchNew from "lib/components/searchNew.svelte";
  import { InfoBlock } from "paxray-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  export let open = false;
  export let applications = [];

  const dispatch = createEventDispatcher();

  let wasOpen = false;
  let step = 1;
  let teamSearch = "";
  let applicationSearch = "";
  let selectedTeamKeys = [];
  let selectedApplicationIds = [];
  let selectedApplications = [];
  let teamsInitialized = false;
  let damTeams = [];
  let damUsers = [];
  let catalogTeams = [];
  let teamOptions = [];
  let selectableTeamKeys = [];
  let selectedTeams = [];
  let selectedRecipients = [];
  let selectedSavings = 0;
  let recipientsLoading = false;
  let recipientsError = "";
  let applicationPage = 1;
  let applicationPageSize = 5;
  let teamPage = 1;
  let teamPageSize = 5;
  let applicationSelectionSignature = "";
  let teamSelectionSignature = "";
  let previousApplicationSearch = "";
  let previousTeamSearch = "";

  $: positiveApplications = applications
    .filter((application) => Number(application.license_savings_cost) > 0)
    .sort(
      (a, b) =>
        Number(b.license_savings_cost) - Number(a.license_savings_cost),
    );
  $: filteredTeams = teamOptions.filter(
    (team) =>
      team.recipients.length > 0 &&
      team.name.toLowerCase().includes(teamSearch.trim().toLowerCase()),
  );
  $: filteredApplications = positiveApplications.filter((application) =>
    String(application.application || "")
      .toLowerCase()
      .includes(applicationSearch.trim().toLowerCase()),
  );
  $: primaryDisabled =
    recipientsLoading ||
    (step === 1
      ? selectedApplicationIds.length === 0
      : selectedTeamKeys.length === 0 || selectedRecipients.length === 0);

  $: {
    const signature = selectedApplicationIds.join("|");
    if (signature !== applicationSelectionSignature) {
      applicationSelectionSignature = signature;
      syncSelectedApplications();
    }
  }
  $: {
    const signature = selectedTeamKeys.join("|");
    if (signature !== teamSelectionSignature) {
      teamSelectionSignature = signature;
      syncTeamSelection();
    }
  }
  $: if (applicationSearch !== previousApplicationSearch) {
    previousApplicationSearch = applicationSearch;
    applicationPage = 1;
  }
  $: if (teamSearch !== previousTeamSearch) {
    previousTeamSearch = teamSearch;
    teamPage = 1;
  }
  $: {
    if (open && !wasOpen) initialize();
    wasOpen = open;
  }

  function normalize(value) {
    return String(value || "").trim().toLowerCase();
  }

  function uniqueValues(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b),
    );
  }

  function applicationId(application) {
    return String(
      application.app_mapping_id || application.id || application.application,
    );
  }

  function syncSelectedApplications() {
    const selectedIds = new Set(selectedApplicationIds);
    selectedApplications = positiveApplications.filter((application) =>
      selectedIds.has(applicationId(application)),
    );
    selectedSavings = selectedApplications.reduce(
      (total, application) =>
        total + Math.max(Number(application.license_savings_cost) || 0, 0),
      0,
    );
    catalogTeams = uniqueValues(
      selectedApplications.flatMap((application) => application.teams || []),
    );
    syncTeamOptions();
  }

  function syncTeamOptions() {
    const usersById = new Map(
      damUsers.map((user) => [String(user.id), user]),
    );
    teamOptions = buildTeamOptions(catalogTeams, damTeams, usersById);
    selectableTeamKeys = teamOptions
      .filter((team) => team.recipients.length > 0)
      .map((team) => team.key);
    syncTeamSelection();
  }

  function syncTeamSelection() {
    selectedTeams = teamOptions.filter((team) =>
      selectedTeamKeys.includes(team.key),
    );
    selectedRecipients = uniqueRecipients(
      selectedTeams.flatMap((team) => team.recipients),
    );
  }

  function userId(user) {
    return String(user?.id || user || "");
  }

  function displayName(user) {
    const explicitName = [
      user?.first_name || user?.firstName,
      user?.last_name || user?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
    const accountName = String(user?.name || "").split("\\").pop();
    return explicitName || accountName || user?.email || "Unknown user";
  }

  function buildTeamOptions(teamNames, teams, usersById) {
    return teamNames.map((name) => {
      const matchingTeams = teams.filter(
        (team) => normalize(team.name) === normalize(name),
      );
      const recipients = uniqueRecipients(
        matchingTeams.flatMap((team) =>
          (team.damUsers || [])
            .map((user) => usersById.get(userId(user)) || user)
            .filter(
              (user) =>
                user &&
                userId(user) &&
                String(user.email || "").trim() &&
                user.email !== "-",
            ),
        ),
      );
      return {
        key: normalize(name),
        name,
        damTeamIds: matchingTeams.map((team) => String(team.id)),
        projectIds: uniqueValues(
          matchingTeams.map((team) => String(team.idproject || "")),
        ),
        recipients,
      };
    });
  }

  function uniqueRecipients(users) {
    const recipients = new Map();
    users.forEach((user) => {
      const key = normalize(user?.email) || userId(user);
      if (key && !recipients.has(key)) recipients.set(key, user);
    });
    return [...recipients.values()];
  }

  async function initialize() {
    step = 1;
    teamSearch = "";
    applicationSearch = "";
    selectedTeamKeys = [];
    selectedApplicationIds = positiveApplications.map(applicationId);
    syncSelectedApplications();
    teamsInitialized = false;
    await loadRecipients();
  }

  async function loadRecipients() {
    recipientsLoading = true;
    recipientsError = "";
    try {
      const [projectsResponse, usersResponse] = await Promise.all([
        fetchWithSession("/api/monitoring/projects"),
        fetchWithSession("/api/damprojects/getDAMTeamUsers"),
      ]);
      const [projectsData, usersData] = await Promise.all([
        projectsResponse.json(),
        usersResponse.json(),
      ]);
      if (!projectsResponse.ok || !usersResponse.ok) {
        throw new Error("Team recipients could not be loaded.");
      }
      const projectIds = (Array.isArray(projectsData) ? projectsData : [])
        .map((project) => String(project.id || ""))
        .filter(Boolean);
      const teamResponses = await Promise.all(
        projectIds.map(async (projectId) => {
          const response = await fetchWithSession(
            `/api/damprojects/dam-teams?idproject=${encodeURIComponent(projectId)}`,
          );
          const data = await response.json();
          if (!response.ok) {
            throw new Error("Team recipients could not be loaded.");
          }
          return (Array.isArray(data) ? data : []).map((team) => ({
            ...team,
            idproject: team.idproject || projectId,
          }));
        }),
      );
      damTeams = teamResponses.flat();
      damUsers = Array.isArray(usersData) ? usersData : [];
      syncTeamOptions();
    } catch (error) {
      damTeams = [];
      damUsers = [];
      recipientsError =
        error.message || "Team recipients could not be loaded.";
    } finally {
      recipientsLoading = false;
    }
  }

  function handlePrimaryAction() {
    if (step === 1) {
      openTeamStep();
      return;
    }

    const applicationsForTeam = (team) =>
      selectedApplications.filter((application) =>
        (application.teams || []).some(
          (applicationTeam) => normalize(applicationTeam) === team.key,
        ),
      );
    const assignments = selectedTeams.map((team) => ({
      team: team.name,
      dam_team_ids: team.damTeamIds,
      project_ids: team.projectIds,
      recipient_ids: team.recipients.map((recipient) => String(recipient.id)),
      application_ids: applicationsForTeam(team).map(applicationId),
    }));

    dispatch("submit", {
      teams: selectedTeams.map((team) => ({
        name: team.name,
        dam_team_ids: team.damTeamIds,
        project_ids: team.projectIds,
      })),
      applications: selectedApplications.map((application) => ({
        id: applicationId(application),
        app_mapping_id: application.app_mapping_id || null,
        name: application.application,
        teams: (application.teams || []).filter((team) =>
          selectedTeamKeys.includes(normalize(team)),
        ),
        license_savings_cost:
          Number(application.license_savings_cost) || 0,
      })),
      recipients: selectedRecipients.map((recipient) => ({
        id: String(recipient.id),
        name: displayName(recipient),
        first_name: recipient.first_name || recipient.firstName || null,
        last_name: recipient.last_name || recipient.lastName || null,
        email: String(recipient.email).trim(),
      })),
      assignments,
      summary: {
        teams: selectedTeams.length,
        applications: selectedApplications.length,
        recipients: selectedRecipients.length,
        license_savings_cost: selectedSavings,
      },
    });
    open = false;
  }

  function openTeamStep() {
    selectedTeamKeys = teamsInitialized
      ? selectedTeamKeys.filter((key) => selectableTeamKeys.includes(key))
      : [...selectableTeamKeys];
    syncTeamSelection();
    teamsInitialized = true;
    step = 2;
    teamSearch = "";
  }

  function formatCurrency(value) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);
  }

  function applicationRows(items) {
    return items.map((application) => ({
      id: applicationId(application),
      application: application.application,
      teams: (application.teams || []).length,
      users: application.users || 0,
      savings: formatCurrency(application.license_savings_cost),
    }));
  }

  function teamRows(items) {
    return items.map((team) => ({
      id: team.key,
      team: team.name,
      applications: selectedApplications.filter((application) =>
        (application.teams || []).some(
          (applicationTeam) => normalize(applicationTeam) === team.key,
        ),
      ).length,
      recipients: team.recipients.length,
    }));
  }
</script>

<Modal
  bind:open
  modalHeading="Generate Survey"
  subtitle="Select the teams and applications to include in the survey."
  actions
  width={920}
  autowidth
  modalClass="generate-survey-modal-shell"
  primaryButtonText={step === 1 ? "Continue" : "Prepare Survey"}
  primaryButtonVariant="secondary"
  secondaryButtonText="Cancel"
  noSecondaryButton
  primaryButtonValidations={primaryDisabled}
  primaryButtonAction={handlePrimaryAction}
>
  <svelte:fragment slot="left-buttons">
    <div class="left-actions">
      <Button text="Cancel" variant="secondary" on:click={() => (open = false)} />
    </div>
  </svelte:fragment>

  <svelte:fragment slot="action-buttons">
    {#if step === 2}
      <Button text="Back" variant="secondary" on:click={() => (step = 1)} />
    {/if}
  </svelte:fragment>

  <div class="survey-modal">
    {#if step === 1}
      <div class="section-heading">
        <div>
          <h4>Which applications should be included?</h4>
          <p>Only applications with savings potential are shown and selected by default.</p>
        </div>
      </div>

      <div class="search-field">
        <SearchNew placeholder="applications" bind:searchTerm={applicationSearch} flexGrow />
      </div>

      {#if filteredApplications.length === 0}
        <div class="empty-state">No applications with savings potential match your search.</div>
      {:else}
        <div class="survey-table">
          <DataTableBlack
            size="short"
            headers={[
              { key: "application", value: "Application" },
              { key: "teams", value: "Teams" },
              { key: "users", value: "Users" },
              { key: "savings", value: "Potential Savings (p.a.)" },
            ]}
            rows={applicationRows(filteredApplications)}
            totalItems={filteredApplications.length}
            showToolbar={false}
            showNewButton={false}
            slicePage
            pageSizes={[5, 10, 25]}
            bind:page={applicationPage}
            bind:pageSize={applicationPageSize}
            bind:selectedRowIds={selectedApplicationIds}
            sortable
            batchSelection
          >
            <svelte:fragment slot="cell" let:cell>
              {cell.value ?? "-"}
            </svelte:fragment>
          </DataTableBlack>
        </div>
      {/if}
    {:else}
      <div class="summary-bar">
        <span><strong>{selectedTeams.length}</strong> teams</span>
        <span><strong>{selectedApplications.length}</strong> applications</span>
        <span><strong>{selectedRecipients.length}</strong> recipients</span>
        <span class="savings"><strong>{formatCurrency(selectedSavings)}</strong> potential savings</span>
      </div>

      <div class="section-heading">
        <div>
          <h4>Which teams should be surveyed?</h4>
          <p>Only teams using a selected application are shown.</p>
        </div>
      </div>

      {#if recipientsError}
        <InfoBlock
          type="error"
          heading="Recipients unavailable"
          message={recipientsError}
          showClose={false}
        />
      {/if}

      <div class="search-field">
        <SearchNew placeholder="teams" bind:searchTerm={teamSearch} flexGrow />
      </div>

      {#if recipientsLoading}
        <div class="loading-state">
          <InlineLoading description="Loading teams and recipients..." />
        </div>
      {:else if filteredTeams.length === 0}
        <div class="empty-state">No teams match your search.</div>
      {:else}
        <div class="survey-table">
          <DataTableBlack
            size="short"
            headers={[
              { key: "team", value: "Team" },
              { key: "applications", value: "Selected Applications" },
              { key: "recipients", value: "Recipients" },
            ]}
            rows={teamRows(filteredTeams)}
            totalItems={filteredTeams.length}
            showToolbar={false}
            showNewButton={false}
            slicePage
            pageSizes={[5, 10, 25]}
            bind:page={teamPage}
            bind:pageSize={teamPageSize}
            bind:selectedRowIds={selectedTeamKeys}
            sortable
            batchSelection
          >
            <svelte:fragment slot="cell" let:cell>
              {cell.value ?? "-"}
            </svelte:fragment>
          </DataTableBlack>
        </div>
      {/if}
    {/if}
  </div>
</Modal>

<style>
  .survey-modal {
    display: flex;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    gap: 1rem;
  }

  .left-actions {
    display: flex;
    gap: 0.5rem;
  }

  .section-heading,
  .summary-bar {
    display: flex;
    align-items: center;
  }

  .section-heading {
    width: 100%;
    justify-content: space-between;
    gap: 1rem;
  }

  .section-heading > div {
    width: 100%;
  }

  h4,
  p {
    margin: 0;
  }

  h4 {
    margin-bottom: 0.25rem;
    color: var(--pax-black);
    font-family: Inter, sans-serif;
    font-size: 0.875rem !important;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }

  p {
    color: var(--pax-black);
    font-family: Inter, sans-serif;
    font-size: 0.875rem !important;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .search-field :global(.search_wrapper) {
    width: 100%;
    min-width: 0;
  }

  .search-field :global(.search) {
    height: 2.5rem;
    padding: 0 0.75rem;
    border-radius: 0.2rem;
  }

  .search-field :global(.searchicon) {
    height: 1.25rem;
  }

  .search-field :global(input) {
    height: 2.25rem;
    padding-left: 0.75rem;
    box-shadow: none !important;
  }

  .search-field :global(input:focus) {
    box-shadow: none !important;
  }

  .survey-table {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    border-radius: 0.5rem;
    background: var(--pax-white);
  }

  .summary-bar {
    flex-wrap: wrap;
    gap: 0.5rem 1.25rem;
    padding: 0.8rem 1rem;
    border-radius: 0.5rem;
    background: var(--pax-smoke, #f7f8fa);
    font-size: 0.85rem;
  }

  .summary-bar .savings {
    margin-left: auto;
  }

  .loading-state,
  .empty-state {
    display: grid;
    min-height: 10rem;
    place-items: center;
    border: 1px dashed var(--pax-border-color, #e2e8f0);
    border-radius: 0.5rem;
    color: #6b7280;
  }

  :global(.generate-survey-modal-shell .bx--modal-container) {
    width: min(64rem, calc(100vw - 2rem)) !important;
  }

  :global(.generate-survey-modal-shell .modal_wrapper) {
    width: 100% !important;
  }

  @media (max-width: 48rem) {
    .summary-bar .savings {
      width: 100%;
      margin-left: 0;
    }
  }
</style>
